import _ from 'lodash'
import Vue from 'vue'
import store from '../store'
import Router from 'vue-router'
import Login from '@/components/Login'
import Dashboard from '@/components/Dashboard'
import Companies from '@/components/Companies'
import Accounts from '@/components/Accounts'
import Account from '@/components/Account/Account'
import Users from '@/components/Users'
import Subscriptions from '@/components/Subscriptions'
import Roles from '@/components/Roles'
import Settings from '@/components/Settings'
import Profile from '@/components/Profile'
import PageNotFound from '@/components/Error/404'
import Maintenance from '@/components/Error/Maintenance'
import ForgotPassword from '@/components/ForgotPassword'
import ResetPassword from '@/components/ResetPassword'
import Invitation from '@/components/Invitation'
import AccountShare from '@/components/AccountShare'
import Modal from '@/components/Elements/Modal'
import { INIT_GET } from '@/store/actions/init'
import { AUTH_CLEAR_TOKEN } from '@/store/actions/auth'

Vue.use(Router)

// Only allow access if user is NOT authenticated
// This prevents authenticated users from accessing the route
const ifNotAuthenticated = (to, from, next) => {
  if (!store.getters.isAuthenticated) {
    next()
    return
  }
  next('/')
}

const ifNotAuthenticatedAsUser = (to, from, next) => {
  if (!store.getters.isAuthenticated || store.getters.init.auth_user.via_invite || store.getters.init.auth_user.via_share_link) {
    next()
    return
  }
  next('/')
}

// If the user is authenticated, continue
const ifAuthenticated = (to, from, next) => {
  const loginPath = window.location.pathname

  // Don't redirect back to maintenance page
  let queryString = { r: loginPath }
  if (loginPath === '/maintenance' || loginPath === '/' || loginPath === '/login') {
    queryString = undefined
  }

  if (store.getters.isAuthenticated) {
    let accessibleFeature = _.get(to, 'matched[0].props.default.accessible_feature')
    if (accessibleFeature !== undefined && store.getters.init !== false) {
      if (!store.getters.userCan(accessibleFeature, 'init')) {
        next('/404')
        return
      }
    }

    next()
    return
  }

  next({ name: 'login', query: queryString })
}

const routes = [
  {
    path: '*',
    name: 'PageNotFound',
    component: PageNotFound
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: Maintenance
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    beforeEnter: ifNotAuthenticated
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
    beforeEnter: ifNotAuthenticated
  },
  {
    path: '/reset-password/:token?',
    name: 'reset-password',
    component: ResetPassword,
    beforeEnter: ifNotAuthenticatedAsUser
  },
  {
    path: '/',
    name: 'dashboard',
    component: Dashboard,
    beforeEnter: ifAuthenticated,
    props: {
      default: true,
      pageType: 'Dashboard',
      accessible_feature: 'view_dashboard_component'
    },
    children: [
      {
        path: 'quickview/accounts/:id',
        name: 'quickview-account',
        component: Modal,
        props: { default: true, modalType: 'accounts' }
      },
      {
        path: 'quickview/accounts/:id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'accounts' }
      }
    ]
  },
  {
    path: '/companies/',
    name: 'companies',
    component: Companies,
    beforeEnter: ifAuthenticated,
    props: {
      default: true,
      pageType: 'companies',
      accessible_feature: 'view_companies_component'
    },
    children: [
      {
        path: 'create',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'companies' }
      },
      {
        path: ':id',
        component: Modal,
        props: { default: true, modalType: 'company' }
      },
      {
        path: ':id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'companies' }
      },
      {
        path: ':id/users/:user_id',
        component: Modal,
        props: { default: true, modalType: 'users' }
      },
      {
        path: ':id/users/:user_id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'users' }
      },
      {
        path: ':id/subscriptions/:subscription_id',
        // name: 'subscriptions',
        component: Modal,
        props: { default: true, modalType: 'subscriptions' }
      },
      {
        path: ':id/subscriptions/:subscription_id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'subscriptions' }
      }
    ]
  },
  {
    path: '/accounts/',
    name: 'accounts',
    component: Accounts,
    beforeEnter: ifAuthenticated,
    props: {
      default: true,
      pageType: 'accounts',
      accessible_feature: 'view_accounts_component'
    },
    children: [
      {
        path: 'create',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'accounts' }
      },
      {
        path: ':id/edit',
        component: Modal,
        name: 'accounts.edit',
        props: { default: true, modalType: 'form', data: 'accounts' }
      }
    ]
  },
  {
    path: '/accounts/:id/',
    component: Account,
    name: 'account-dashboard',
    props: { default: true, pageType: 'account' },
    beforeEnter: ifAuthenticated,
    children: [
      {
        path: 'properties/create',
        component: Modal,
        name: 'account-properties-create',
        props: { default: true, modalType: 'form', data: 'account_properties' }
      },
      {
        path: 'properties/:prop_id',
        component: Modal,
        name: 'account-properties',
        props: { default: true, modalType: 'account-properties' }
      },
      {
        path: 'properties/:prop_id/upload',
        component: Modal,
        name: 'account-properties-upload',
        props: { default: true, modalType: 'account-properties', upload: true }
      },
      {
        path: 'properties/:prop_id/edit',
        component: Modal,
        name: 'account-form',
        props: { default: true, modalType: 'form', data: 'account_properties' }
      },
      {
        path: 'create',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'account_properties' }
      },
      {
        path: 'import',
        component: Modal,
        name: 'account-import',
        props: { default: true, modalType: 'property-import', small: true }
      },
      {
        path: 'upload',
        component: Modal,
        name: 'account-files',
        props: { default: true, modalType: 'account-files', small: true, upload: true }
      },
      {
        path: 'files/:file_id',
        component: Modal,
        name: 'account-file',
        props: { default: true, modalType: 'account-files', small: true, upload: false }
      },
      {
        path: 'edit-overview',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'accounts' }
      },
      {
        path: 'invites/create',
        name: 'account-invites-create',
        component: Modal,
        props: { default: true, modalType: 'account-invite', small: true }
      },
      {
        path: 'properties/:prop_id/risk_control/create',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'risk_control' }
      },
      {
        path: 'properties/:prop_id/risk_control/:risk_control_id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'risk_control' }
      },
      {
        path: 'properties/:prop_id/risk_control/:risk_control_id',
        component: Modal,
        props: { default: true, modalType: 'risk-control-report' }
      }
    ]
  },
  {
    path: '/users/',
    name: 'users',
    component: Users,
    beforeEnter: ifAuthenticated,
    props: {
      default: true,
      pageType: 'users',
      accessible_feature: 'view_users_component'
    },
    children: [
      {
        path: 'users-company/:id',
        component: Modal,
        props: { default: true, modalType: 'company' }
      },
      {
        path: 'create',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'users' }
      },
      {
        path: ':id',
        component: Modal,
        props: { default: true, modalType: 'users' }
      },
      {
        path: ':id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'users' }
      }
    ]
  },
  {
    path: '/subscriptions/',
    name: 'subscriptions',
    component: Subscriptions,
    beforeEnter: ifAuthenticated,
    props: {
      default: true,
      pageType: 'subscriptions',
      accessible_feature: 'view_subscriptions_component'
    },
    children: [
      {
        path: 'create',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'subscriptions' }
      },
      {
        path: ':id',
        component: Modal,
        props: { default: true, modalType: 'subscriptions' }
      },
      {
        path: ':id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'subscriptions' }
      }
    ]
  },
  {
    path: '/roles/',
    name: 'roles',
    component: Roles,
    beforeEnter: ifAuthenticated,
    props: {
      default: true,
      pageType: 'roles',
      accessible_feature: 'view_roles_component'
    },
    children: [
      {
        path: 'create',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'roles' }
      },
      {
        path: ':id',
        component: Modal,
        props: { default: true, modalType: 'roles' }
      },
      {
        path: ':id/edit',
        component: Modal,
        props: { default: true, modalType: 'form', data: 'roles' }
      }
    ]
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/profile',
    name: 'profile',
    component: Profile,
    beforeEnter: ifAuthenticated
  },
  {
    path: '/invite/:inviteId',
    name: 'invite-validation',
    component: Invitation
  },
  {
    path: '/account/:accountId/share/:shareToken',
    name: 'account-share',
    component: AccountShare
  },
  {
    path: '/#',
    name: 'logout',
    beforeEnter: ifAuthenticated
  },
  {
    path: '*',
    name: '404',
    component: 404
  }
]

const router = new Router({
  routes: routes,
  mode: 'history',
  scrollBehavior (to, from, savedPosition) {
    // Only scroll to top when moving to a new page
    if (to.name === from.name) return
    if (to.path.split('/')[1] === from.path.split('/')[1]) return

    let element = document.getElementById('app')
    if (typeof (element) !== 'undefined' && element != null) {
      document.getElementById('app').scrollTop = 0
      return { x: 0, y: 0 }
    }
  }
})

router.beforeEach((to, from, next) => {
  return new Promise(resolve => {
    if (store.getters.isAuthenticated === true && store.getters.init === false) {
      store.dispatch(INIT_GET).then(() => {
        next()
        resolve()
      }).catch(() => {
        store.dispatch(AUTH_CLEAR_TOKEN)
        next({ name: 'login' })
      })
    } else {
      next()
      resolve()
    }
  })
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js